import { PiCheck, PiX } from 'react-icons/pi';
import { Avatar } from 'src/modules/shared/components/atoms/avatar';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Text } from 'src/modules/shared/components/atoms/text';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { formatTimeDistance } from 'src/modules/shared/utils/formatting/date.util';
import { buildCatalogPropertyTextViewModel } from 'src/modules/shared/utils/catalog/property-summary.util';
import type { MessageNotificationCardProps } from '../../types/notification-presentation.types';

/** Shared contextual card used by transient alerts and the notification inbox. */
export const MessageNotificationCard = ({
  group,
  onOpen,
  onDismiss,
  onMarkRead,
  markReadDisabled,
  transient = false,
}: MessageNotificationCardProps) => {
  const { t } = useI18n();
  const item = group.latest;
  const message = item.metadata?.message;
  const name = message?.senderName?.trim() ? message.senderName : item.title;
  const property = message?.property;
  const propertyText =
    property &&
    (property.propertyType || property.locality?.trim() || property.operation)
      ? buildCatalogPropertyTextViewModel(property, t, ' · ')
      : undefined;
  return (
    <div
      className={`relative flex min-w-0 items-start gap-2 rounded-2xl border p-3.5 ${transient ? 'border-neutral-200 bg-white shadow-lg' : !item.readAt ? 'border-primary-100 bg-primary-50' : 'border-neutral-200 bg-white'}`}
    >
      <Button
        type="button"
        appearance="ghost"
        variant="neutral"
        onClick={onOpen}
        className="!h-auto min-w-0 flex-1 flex-col !items-stretch !justify-start gap-2 !whitespace-normal !p-0 text-left"
        aria-label={t('notifications.message.openFrom', { name })}
      >
        <div className="flex min-w-0 items-center gap-3">
          <Avatar
            text={(Array.from(name.trim())[0] ?? '').toUpperCase()}
            size="sm"
            variant="secondary"
          />
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <Text
              disableAutoLink
              weight="semibold"
              size="paragraph"
              className="min-w-0 truncate"
              title={name}
            >
              {name}
            </Text>
            {!item.readAt && (
              <span
                aria-hidden="true"
                className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary-500"
              />
            )}
          </div>
        </div>
        <div className="min-w-0">
          <Text
            disableAutoLink
            size="small"
            className="line-clamp-2 break-words text-neutral-900"
          >
            {item.body}
          </Text>
          {propertyText && (
            <div className="mt-2 text-secondary-500">
              <Text
                disableAutoLink
                size="tiny"
                className="truncate"
                title={propertyText.displayTitle}
              >
                {propertyText.displayTitle}
              </Text>
              {propertyText.priceRows.length > 0 && (
                <Text
                  disableAutoLink
                  size="tiny"
                  className="line-clamp-2 break-words"
                >
                  {propertyText.priceRows
                    .map(({ label, value }) =>
                      [label, value].filter(Boolean).join(' ')
                    )
                    .join(' · ')}
                </Text>
              )}
            </div>
          )}
          {group.messageCount > 1 && (
            <Text
              disableAutoLink
              size="tiny"
              weight="medium"
              className="mt-1 text-primary-700"
            >
              {t('notifications.message.groupCount', {
                count: group.messageCount,
              })}
            </Text>
          )}
          {!transient && (
            <Text
              disableAutoLink
              size="tiny"
              className="mt-1 text-secondary-500"
            >
              {formatTimeDistance(new Date(item.createdAt), true)}
            </Text>
          )}
        </div>
      </Button>
      <div className="flex shrink-0 flex-col gap-1">
        {onDismiss && (
          <Button
            appearance="ghost"
            size="xs"
            shape="round"
            isIconOnly
            aria-label={t('notifications.message.dismiss')}
            onClick={onDismiss}
          >
            <PiX className="h-4 w-4" />
          </Button>
        )}
        {onMarkRead && (
          <Button
            appearance="ghost"
            size="xs"
            shape="round"
            isIconOnly
            disabled={Boolean(item.readAt) || markReadDisabled}
            aria-label={t('notifications.message.markRead')}
            onClick={onMarkRead}
          >
            <PiCheck className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};
